import { Close, FilterList } from "@mui/icons-material";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import clsx from "clsx";
import _ from "lodash";
import React, { useMemo, useState } from "react";
import { t } from "ttag";
import { Lookup } from "../../components/Lookup";
import Tooltip from "../../components/Tooltip/LightTooltip";
import { DeleteIcon } from "../../iconImages";
import SimpleModal from "../../widgets/SimpleModal/SimpleModal";
import { IHealthSubscription, ISiteNameIndex } from "../../models/DeviceHealth.types";
import useStyles from "./ManageSubscriptionsPopup.style";

type ColumnId = "customer" | "site" | "email";

const filterableColumns: ColumnId[] = ["customer", "site", "email"];

interface IManageSubscriptionsPopupProps {
  subscriptions: IHealthSubscription[];
  /**
   * site id -> site + customer names, for every site in the system. Must not be
   * derived from the report filters: those only cover sites present in the
   * current snapshot, while a subscription can point at any site.
   */
  siteNames: ISiteNameIndex;
  loading: boolean;
  deleting: boolean;
  onClose: () => void;
  onDelete: (subscriptionId: string) => void;
}

/**
 * The API stores one subscription per (site, email) pair, which lines up with the
 * customer / site / email columns the ticket asks for - each row deletes on its own.
 */
const ManageSubscriptionsPopup: React.FC<IManageSubscriptionsPopupProps> = ({
  subscriptions,
  siteNames,
  loading,
  deleting,
  onClose,
  onDelete
}) => {
  const classes: any = useStyles();

  const [showFilterPopup, setShowFilterPopup] = useState<string>("");
  const [lookupAnchor, setLookupAnchor] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<IHealthSubscription | null>(null);

  const getColumnValue = (subscription: IHealthSubscription, columnId: ColumnId): string => {
    const entry = siteNames[subscription.site];

    switch (columnId) {
      case "customer":
        return entry?.customerName || "-";
      case "site":
        return entry?.siteName || "-";
      case "email":
        return subscription.email;
      default:
        return "";
    }
  };

  const filterOptions = useMemo(() => {
    return filterableColumns.reduce((acc: any, columnId) => {
      acc[columnId] = _.uniq(
        subscriptions.map((subscription) => getColumnValue(subscription, columnId))
      ).sort();

      return acc;
    }, {});
  }, [subscriptions, siteNames]);

  const filteredSubscriptions = useMemo(() => {
    const activeColumns = Object.keys(appliedFilters).filter(
      (columnId) => appliedFilters[columnId]?.length
    );

    if (!activeColumns.length) {
      return subscriptions;
    }

    return subscriptions.filter((subscription) =>
      activeColumns.every((columnId) =>
        appliedFilters[columnId].includes(getColumnValue(subscription, columnId as ColumnId))
      )
    );
  }, [appliedFilters, subscriptions, siteNames]);

  const hasFilters = !!Object.values(appliedFilters).flat().length;

  const handleFilterPopup = (columnId: ColumnId) => (event: any) => {
    setShowFilterPopup(columnId);
    setLookupAnchor(event.currentTarget);
  };

  const handleApplyFilter = (selectedFilters: string[]) => {
    setAppliedFilters({ ...appliedFilters, [showFilterPopup]: selectedFilters });
    setShowFilterPopup("");
  };

  const handleCloseFilterPopup = () => {
    setShowFilterPopup("");
  };

  const handleClearAllFilters = () => {
    setAppliedFilters({});
  };

  const handleDeleteClick = (subscription: IHealthSubscription) => () => {
    setSubscriptionToDelete(subscription);
  };

  const handleCloseDeleteModal = () => {
    setSubscriptionToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!subscriptionToDelete) {
      return;
    }

    onDelete(subscriptionToDelete.id);
    setSubscriptionToDelete(null);
  };

  const renderHeaderCell = (columnId: ColumnId, label: string) => (
    <TableCell className={classes.headCell} onClick={handleFilterPopup(columnId)}>
      <div className={classes.headCellContent}>
        {label}
        <FilterList
          className={clsx(classes.filterIcon, {
            [classes.filterIconActive]: !_.isEmpty(appliedFilters[columnId])
          })}
        />
      </div>
    </TableCell>
  );

  const renderRows = () => {
    if (!filteredSubscriptions.length) {
      return (
        <TableRow>
          <TableCell colSpan={4} className={classes.emptyCell}>
            <Typography className={classes.emptyText}>
              {subscriptions.length
                ? t`No subscriptions match the selected filters.`
                : t`No notification subscriptions yet.`}
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return filteredSubscriptions.map((subscription) => (
      <TableRow hover key={subscription.id}>
        <TableCell className={classes.cell}>{getColumnValue(subscription, "customer")}</TableCell>
        <TableCell className={classes.cell}>{getColumnValue(subscription, "site")}</TableCell>
        <TableCell className={classes.cell}>
          <span className={classes.email}>{subscription.email}</span>
          {subscription.diff && (
            <Typography className={classes.incrementalTag}>{t`Incremental updates`}</Typography>
          )}
          {!!subscription.lastNotifiedReportDate && (
            <Typography className={classes.lastNotified}>
              {t`Last emailed`}: {subscription.lastNotifiedReportDate}
            </Typography>
          )}
        </TableCell>
        <TableCell align="center" className={classes.cell}>
          <Tooltip title={t`Delete subscription`} placement="top">
            <IconButton size="small" onClick={handleDeleteClick(subscription)} disabled={deleting}>
              <img src={DeleteIcon} alt={t`delete subscription`} className={classes.deleteIcon} />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    ));
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress size={32} />
        </div>
      );
    }

    return (
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {renderHeaderCell("customer", t`CUSTOMER NAME`)}
              {renderHeaderCell("site", t`SITE NAME`)}
              {renderHeaderCell("email", t`EMAIL`)}
              <TableCell align="center" className={classes.headCell}>{t`DELETE`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.titleText}>{t`Notification Subscriptions`}</Typography>
        <IconButton size="small" onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>{renderContent()}</DialogContent>

      {!!showFilterPopup && (
        <Lookup
          filtersList={filterOptions[showFilterPopup]}
          appliedFilters={appliedFilters[showFilterPopup]}
          onApply={handleApplyFilter}
          lookupAnchor={lookupAnchor}
          onClose={handleCloseFilterPopup}
          clearAllFilters={handleClearAllFilters}
          hasFilters={hasFilters}
        />
      )}

      <SimpleModal
        showModal={!!subscriptionToDelete}
        title={t`Delete Subscription`}
        contentText={t`Are you sure you want to delete this notification subscription?`}
        actionName={t`Delete`}
        onAction={handleConfirmDelete}
        closeModal={handleCloseDeleteModal}
      />
    </Dialog>
  );
};

export default ManageSubscriptionsPopup;
